import { FC, useMemo } from 'react';

import { DocumentTextIcon, DownloadIcon } from '@heroicons/react/outline';
import { GiftRebateSettlement } from '@shared/api/giftRebateSettlement/giftRebateSettlement.interface';
import { GiftSettlementStatus } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';
import { Loading } from '@shared/components/Loading';

import {
  computeRebateTotals,
  RebateRowCalc,
  settlementLineToRebateRow,
} from '../../utils/rebate-table.utils';
import { exportRebateRowsExcel } from '../../utils/rebateSettlementExcel';
import { RebateSettlementTable } from './RebateSettlementTable';

// ─────────────────────────────────────────────
// RebateDraftSection
//
// 정산 예정 = admin이 생성한 상조정산 문서 중 status === 'Draft'(미지급) 건.
// 정산 문서의 라인(lines[])을 펼쳐 테이블 행으로 변환한다.
// (라인 → 행 매핑은 settlementLineToRebateRow 가 담당 — 완료 내역 상세 모달과 공유)
// ─────────────────────────────────────────────

/** `RebateDraftSection` 컴포넌트의 props */
export interface RebateDraftSectionProps {
  /** 상조정산 완료 내역 (이 중 status === 'Draft'만 정산 예정으로 표시) */
  historyItems: GiftRebateSettlement[] | undefined;
  /** 데이터 로딩 중 여부 */
  loading: boolean;
}

export const RebateDraftSection: FC<RebateDraftSectionProps> = ({
  historyItems,
  loading,
}) => {
  // status === 'Draft'인 정산 문서만 정산 예정으로 취급
  const draftSettlements = useMemo(
    () => (historyItems ?? []).filter((s) => s.status === GiftSettlementStatus.Draft),
    [historyItems],
  );

  // Draft 문서들의 라인을 펼쳐 테이블 행으로 변환 (완료 내역 상세 모달과 동일 매핑)
  const rows = useMemo<RebateRowCalc[]>(
    () => draftSettlements.flatMap((s) => s.lines.map(settlementLineToRebateRow)),
    [draftSettlements],
  );

  const totals = useMemo(() => computeRebateTotals(rows), [rows]);

  const hasRows = rows.length > 0;

  // 정산 예정일: Draft 정산들 중 가장 최신 (year, month) + 1개월 (12월이면 다음해 1월),
  // 날짜는 15일 고정. 연·월을 함께 비교해야 (26,12)와 (27,1) 중 27,1을 최신으로 고른다.
  const settlementDate = useMemo(() => {
    if (draftSettlements.length === 0) return null;
    // year*12 + month 단조 키로 최신 정산을 찾는다
    const latest = draftSettlements.reduce((acc, s) =>
      s.year * 12 + s.month > acc.year * 12 + acc.month ? s : acc,
    );
    let year = latest.year;
    let month = latest.month + 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
    return { year, month, day: 15 };
  }, [draftSettlements]);

  return (
    <div>
      <div className="mb-1 flex items-start justify-between gap-2">
        <h3 className="font-semibold text-gray-800">
          정산 예정{hasRows && `(${totals.totalCount}건)`}
          {settlementDate && (
            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-indigo-50 px-3 py-1 text-sm font-bold text-indigo-700 align-middle">
              정산 예정일 {settlementDate.year}년 {settlementDate.month}월{' '}
              {settlementDate.day}일
            </span>
          )}
        </h3>
        {hasRows && (
          <button
            onClick={() =>
              exportRebateRowsExcel(
                rows,
                totals,
                `상조정산예정${
                  settlementDate
                    ? `_${settlementDate.year}년${settlementDate.month}월`
                    : ''
                }.xlsx`,
                '정산예정',
              )
            }
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            <DownloadIcon className="h-4 w-4" />
            엑셀 다운로드
          </button>
        )}
      </div>
      {loading ? (
        <div className="center-box py-16">
          <Loading />
        </div>
      ) : hasRows ? (
        <>
          {/* 요약 카드 */}
          <div className="mt-4 mb-4 grid grid-cols-4 gap-3">
            <div className="flex flex-col justify-center rounded-lg bg-gray-50 p-4 text-center">
              <p className="text-2xl font-bold text-gray-700">
                {totals.totalPriceRetail.toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">총 판매가</p>
            </div>
            <div className="flex flex-col justify-center rounded-lg bg-gray-50 p-4 text-center">
              <p className="text-2xl font-bold text-gray-700">
                {totals.totalPriceWhole.toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">총 상품원가</p>
            </div>
            <div className="flex flex-col justify-center rounded-lg bg-gray-50 p-4 text-center">
              <p className="text-2xl font-bold text-gray-700">
                {totals.totalFee.toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">총 수수료</p>
            </div>
            <div className="flex flex-col justify-center rounded-lg bg-gray-50 p-4 text-center">
              <p className="text-2xl font-bold text-rose-700">
                {totals.totalAmount.toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">총 수익금 (VAT 포함)</p>
            </div>
          </div>

          <RebateSettlementTable
            rows={rows}
            totals={totals}
            emptyMessage="정산 예정(Draft) 건이 없습니다."
          />
        </>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50/60 px-6 py-16 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-indigo-50">
            <DocumentTextIcon className="h-8 w-8 text-indigo-400" />
          </div>
          <p className="text-base font-semibold text-gray-700">
            현재 정산 생성 내역이 없습니다
          </p>
          <p className="mt-2 max-w-sm text-sm leading-relaxed text-gray-500">
            정산 내역은 매월 말 생성되며,
            <br />
            미정산 내역은{' '}
            <span className="font-medium text-indigo-600">미정산배송내역</span> 탭에서
            확인 가능합니다.
          </p>
        </div>
      )}
    </div>
  );
};
